import type { LookupItem } from '../mocks/criminalMasterData';
import { EMPTY_CELL } from '../data/recordRoom';
import { getFileCategories } from '../services/criminalCaseRequests';
import {
  isPublicPetitionCategory,
  setPublicPetitionCategoryId,
} from '../pages/wizard/publicPetition/publicPetitionConstants';
import {
  isRightToInformationCategory,
  setRightToInformationCategoryId,
} from '../pages/wizard/rightToInformation/rightToInformationConstants';

type FileCategoryRef = {
  id?: string;
  name?: string;
  code?: string;
};

/** Table cells use em dash for empty values; treat as missing for category matching. */
export function normalizeFileCategoryLabel(name: string | undefined): string {
  const trimmed = (name ?? '').trim();
  return trimmed === EMPTY_CELL ? '' : trimmed;
}

let exemptCategoryIdsPromise: Promise<void> | null = null;

/**
 * Load RTI / Public Petition category ids from master data so id-based exempt checks work
 * outside the case-request wizards (e.g. Assign Tasks configure assignment).
 */
export function ensureExemptSubFileCategoryIdsLoaded(): Promise<void> {
  if (!exemptCategoryIdsPromise) {
    exemptCategoryIdsPromise = (async () => {
      const res = await getFileCategories();
      if (!res.success) return;
      for (const category of res.data) {
        if (isRightToInformationCategory(category) && category.id) {
          setRightToInformationCategoryId(category.id);
        }
        if (isPublicPetitionCategory(category) && category.id) {
          setPublicPetitionCategoryId(category.id);
        }
      }
    })();
  }
  return exemptCategoryIdsPromise;
}

/** Mapping sheet: Right to Information and Public Petitions have no sub file categories. */
export function fileCategoryExemptFromSubFileCategory(item: FileCategoryRef): boolean {
  return (
    isRightToInformationCategory({
      id: item.id,
      name: normalizeFileCategoryLabel(item.name),
      code: item.code,
    }) ||
    isPublicPetitionCategory({
      id: item.id,
      name: normalizeFileCategoryLabel(item.name),
      code: item.code,
    })
  );
}

export function subFileCategoryIsRequired(
  subCategories: readonly LookupItem[],
  loadingSubCategories = false,
): boolean {
  if (loadingSubCategories) return false;
  return subCategories.length > 0;
}

type FileCategoryDraft = {
  fileCategoryId: string;
  fileCategory: string;
};

export type SubFileCategoryValidationContext = {
  subCategories?: readonly LookupItem[];
  loadingSubCategories?: boolean;
  fileCategoryCode?: string;
};

function resolveFileCategoryRef(
  draft: FileCategoryDraft,
  options?: SubFileCategoryValidationContext,
): FileCategoryRef {
  return {
    id: draft.fileCategoryId,
    name: draft.fileCategory,
    code: options?.fileCategoryCode,
  };
}

/** Whether step validation should require a sub file category for the current file category. */
export function draftRequiresSubFileCategory(
  draft: FileCategoryDraft,
  options?: SubFileCategoryValidationContext,
): boolean {
  if (fileCategoryExemptFromSubFileCategory(resolveFileCategoryRef(draft, options))) return false;

  if (options?.subCategories !== undefined) {
    return subFileCategoryIsRequired(options.subCategories, options.loadingSubCategories);
  }

  return true;
}

export function subFileCategoryRequiredForCategory(
  category: FileCategoryRef,
  subCategories: readonly LookupItem[],
  loadingSubCategories = false,
): boolean {
  if (fileCategoryExemptFromSubFileCategory(category)) return false;
  return subFileCategoryIsRequired(subCategories, loadingSubCategories);
}
